import {$isListItemNode, $isListNode} from '@lexical/list';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$findMatchingParent} from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import {useEffect} from 'react';

/**
 * Plugin to handle backspace on empty list items in nested lists more gracefully.
 *
 * The default Lexical behavior can sometimes remove too much content when
 * backspacing on empty list items in deeply nested lists. This plugin
 * intercepts backspace and ensures only the current list item is affected.
 */
export const ListBackspacePlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const anchorOffset = selection.anchor.offset;

        // Find the list item containing the cursor
        const listItemNode = $findMatchingParent(anchorNode, $isListItemNode);

        if (!listItemNode) {
          return false;
        }

        // Check if cursor is at the start of the list item
        const isAtStart =
          anchorOffset === 0 &&
          ($isTextNode(anchorNode) ? anchorNode.getPreviousSibling() === null : true);

        if (!isAtStart) {
          return false;
        }

        // Check if the list item is empty or contains only whitespace
        const textContent = listItemNode.getTextContent();
        const isEmpty = textContent.trim().length === 0;

        // Check if this list item has nested content (a nested list inside it)
        const hasNestedList = listItemNode.getChildren().some((child) => $isListNode(child));

        // Only handle empty list items without nested content
        if (!isEmpty || hasNestedList) {
          return false;
        }

        // Get parent list
        const parentList = listItemNode.getParent();

        if (!$isListNode(parentList)) {
          return false;
        }

        // Get the parent of the list (could be another list item for nested lists)
        const listParent = parentList.getParent();

        event.preventDefault();

        editor.update(() => {
          const prevSibling = listItemNode.getPreviousSibling();
          const nextSibling = listItemNode.getNextSibling();

          // If this is the only item in the list
          if (!prevSibling && !nextSibling) {
            // If nested inside another list item, just remove this list
            if ($isListItemNode(listParent)) {
              // Remove the empty nested list entirely
              parentList.remove();
              // Move selection to the parent list item
              listParent.selectEnd();
            } else {
              // Top-level list with single empty item - convert to paragraph
              const paragraph = $createParagraphNode();
              parentList.replace(paragraph);
              paragraph.select();
            }
          } else {
            // There are other items in the list
            if (prevSibling) {
              // Move to the end of the previous sibling
              if ($isListItemNode(prevSibling)) {
                // Check if prev sibling has a nested list
                const prevChildren = prevSibling.getChildren();
                const lastChild = prevChildren[prevChildren.length - 1];

                if ($isListNode(lastChild)) {
                  // Get the last list item in the nested list
                  const nestedItems = lastChild.getChildren();
                  const lastNestedItem = nestedItems[nestedItems.length - 1];
                  if ($isListItemNode(lastNestedItem)) {
                    lastNestedItem.selectEnd();
                  } else {
                    prevSibling.selectEnd();
                  }
                } else {
                  prevSibling.selectEnd();
                }
              }
              listItemNode.remove();
            } else if (nextSibling) {
              // First item - move to start of next sibling after removing
              listItemNode.remove();
              if ($isListItemNode(nextSibling)) {
                nextSibling.selectStart();
              }
            }
          }
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
};
